<template>
  <div class="flex flex-col h-full min-h-0">
    <ChatUserSearchBar
      placeholder="Find someone to add..."
      @select="onAddFriend"
    />

    <ChatAddByIdInput @added="onAddedByChatId" />

    <!-- Status toast after sending request -->
    <div
      v-if="flash"
      class="mx-3 mt-2 px-3 py-1.5 rounded-md text-[11px]"
      :class="flash.type === 'error'
        ? 'bg-red-500/10 border border-red-500/20 text-red-200'
        : 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-200'"
    >
      {{ flash.message }}
    </div>

    <div class="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-transparent">
      <div v-if="loading && friends.length === 0 && incoming.length === 0" class="flex justify-center py-8">
        <div class="w-6 h-6 border-2 border-white/10 border-t-primary rounded-full animate-spin"></div>
      </div>

      <!-- Incoming requests -->
      <template v-if="incoming.length > 0">
        <div class="px-3 py-2 border-b border-white/5 flex items-center justify-between">
          <span class="text-[10px] uppercase tracking-widest text-text-muted font-semibold">Requests</span>
          <span class="text-[10px] text-text-dim tabular-nums">{{ incoming.length }}</span>
        </div>
        <ChatFriendRow
          v-for="r in incoming"
          :key="r.id"
          :friend="r"
          :busy="isActionInFlight(r.id)"
          @accept="accept"
          @reject="reject"
        />
      </template>

      <!-- Outgoing pending -->
      <template v-if="outgoing.length > 0">
        <div class="px-3 py-2 border-b border-white/5">
          <span class="text-[10px] uppercase tracking-widest text-text-muted font-semibold">Sent</span>
        </div>
        <ChatFriendRow
          v-for="r in outgoing"
          :key="r.id"
          :friend="r"
          :busy="isActionInFlight(r.id)"
          @remove="removeFriend"
        />
      </template>

      <!-- Accepted friends -->
      <template v-if="friends.length > 0">
        <div class="px-3 py-2 border-b border-white/5 flex items-center justify-between gap-2">
          <span class="text-[10px] uppercase tracking-widest text-text-muted font-semibold shrink-0">{{ $t('chat.friends_label') }}</span>
          <button
            type="button"
            class="flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest rounded transition-colors"
            :class="collabOnly
              ? 'bg-cyan-400/20 text-cyan-300 border border-cyan-400/40'
              : 'text-text-muted hover:text-cyan-300 hover:bg-white/5 border border-transparent'"
            :title="$t('chat.filter_collab_only_tooltip')"
            :aria-pressed="collabOnly"
            @click="collabOnly = !collabOnly"
          >
            <span class="w-1.5 h-1.5 rounded-full bg-cyan-400"></span>
            {{ $t('chat.filter_collab_only') }}
          </button>
          <span class="text-[10px] text-text-dim tabular-nums shrink-0">
            {{ collabOnly ? filteredFriends.length + ' / ' + friends.length : friends.length }}
          </span>
        </div>
        <ChatFriendRow
          v-for="f in filteredFriends"
          :key="f.id"
          :friend="f"
          :busy="isActionInFlight(f.id)"
          @message="onOpenDM"
          @remove="removeFriend"
          @block="block"
        />
        <div
          v-if="collabOnly && filteredFriends.length === 0"
          class="px-4 py-6 text-center text-[11px] text-text-dim"
        >
          {{ $t('chat.no_collab_friends') }}
        </div>
      </template>

      <div
        v-if="!loading && friends.length === 0 && incoming.length === 0 && outgoing.length === 0"
        class="text-center py-12 px-4"
      >
        <p class="text-sm text-text-muted">No friends yet</p>
        <p class="text-[11px] text-text-dim mt-1">Search above to send your first friend request.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useFriends } from '~/composables/chat/useFriends'
import { useChatThreads } from '~/composables/chat/useChatThreads'
import { useChatUI } from '~/composables/chat/useChatUI'

const {
  friends, incoming, outgoing, loading,
  sendRequest, accept, reject, removeFriend, block, isActionInFlight
} = useFriends()

// Quick-filter chip above the Friends section: when flipped on, the list
// only shows users who have the "Open for collaborations" flag set. The
// counter chip next to the label becomes "N / total" so the user can see
// how many people the filter collapsed. Persisted in localStorage so a
// tab switch doesn't drop the user back into the unfiltered view.
const COLLAB_FILTER_KEY = 'diztree:chat:collabOnly'
const collabOnly = ref(false)
if (import.meta.client) {
  try { collabOnly.value = localStorage.getItem(COLLAB_FILTER_KEY) === '1' } catch {}
}
watch(collabOnly, (v) => {
  if (!import.meta.client) return
  try { localStorage.setItem(COLLAB_FILTER_KEY, v ? '1' : '0') } catch {}
})

const filteredFriends = computed(() => {
  if (!collabOnly.value) return friends.value
  return friends.value.filter(f => f.user?.openForCollabs)
})

const { startDM } = useChatThreads()
const { setTab, openDMThread } = useChatUI()

const flash = ref(null) // { type: 'ok' | 'error', message: string }

function showFlash(type, message, duration = 3000) {
  flash.value = { type, message }
  setTimeout(() => { if (flash.value && flash.value.message === message) flash.value = null }, duration)
}

async function onAddFriend(user) {
  const res = await sendRequest(user.id)
  if (!res.ok) {
    showFlash('error', res.error === 'Blocked' ? 'That user has blocked you' : ('Failed: ' + res.error))
  } else if (res.autoAccepted) {
    showFlash('ok', `You and ${user.name} are now friends!`)
  } else {
    showFlash('ok', `Request sent to ${user.name}`)
  }
}

async function onOpenDM(userId) {
  const threadId = await startDM(userId)
  if (threadId) {
    setTab('dms')
    openDMThread(threadId)
  }
}

// `ChatAddByIdInput` handles its own success/error hint inline — we just
// surface a toast-style flash so the user sees feedback in the same place
// as name-search additions.
function onAddedByChatId({ autoAccepted }) {
  showFlash('ok', autoAccepted
    ? 'You are now friends!'
    : 'Friend request sent')
}
</script>
